import { createContext, useContext, useState, ReactNode, useMemo, useCallback } from "react";
import { spellDegree, spellPcInScale, spellPc, keyUsesFlats, parseNote } from "@/theory/spell";

/**
 * Course-wide transposition system. A lesson wraps its body in <KeyProvider>
 * and declares which scale qualities are valid (e.g. ["major"] for a Major
 * scale lesson; ["major","natural-minor","harmonic-minor","melodic-minor"]
 * for a lesson that covers minor variants too).
 *
 * Components that subscribe to useKey() can:
 *   • Read the current tonic + quality
 *   • Map scale degrees → absolute pitch classes
 *   • Compute the I/IV/V/etc. roots for the current key
 *   • Stay in sync when the student picks a new key from <KeyPicker />
 *
 * Naming follows standard music-theory convention: sharp keys use ♯ and the
 * sharp letter; flat keys use ♭ and the flat letter; never both. Crucially the
 * choice depends on tonic AND quality — C major takes no accidentals, but
 * C *minor* takes three flats, so its third must print as E♭ and never D♯.
 * All spelling is delegated to `theory/spell.ts`, which works from the scale
 * degree rather than the pitch class.
 */

export type ScaleQuality =
  | "major"
  | "natural-minor"
  | "harmonic-minor"
  | "melodic-minor";

export const QUALITY_LABEL: Record<ScaleQuality, string> = {
  "major": "Major",
  "natural-minor": "Natural Minor",
  "harmonic-minor": "Harmonic Minor",
  "melodic-minor": "Melodic Minor",
};

export const isMinorQuality = (q: ScaleQuality) => q !== "major";

/** Scale-degree labels per quality — the input the speller works from. */
export const QUALITY_DEGREES: Record<ScaleQuality, string[]> = {
  "major":          ["1", "2", "3",  "4", "5", "6",  "7"],
  "natural-minor":  ["1", "2", "♭3", "4", "5", "♭6", "♭7"],
  "harmonic-minor": ["1", "2", "♭3", "4", "5", "♭6", "7"],
  "melodic-minor":  ["1", "2", "♭3", "4", "5", "6",  "7"],
};

export interface TonicEntry { pc: number; name: string; useFlats: boolean }

/**
 * The twelve major keys, in order of key signature: C then the sharps
 * clockwise, then the flats. Six sharps / six flats is where we stop —
 * beyond that the spellings get unwieldy and we use the twin.
 */
export const TONIC_TABLE: TonicEntry[] = [
  { pc: 0,  name: "C",  useFlats: false }, // 0 sharps/flats
  { pc: 7,  name: "G",  useFlats: false }, // 1 sharp
  { pc: 2,  name: "D",  useFlats: false }, // 2 sharps
  { pc: 9,  name: "A",  useFlats: false }, // 3 sharps
  { pc: 4,  name: "E",  useFlats: false }, // 4 sharps
  { pc: 11, name: "B",  useFlats: false }, // 5 sharps
  { pc: 6,  name: "F♯", useFlats: false }, // 6 sharps
  { pc: 5,  name: "F",  useFlats: true  }, // 1 flat
  { pc: 10, name: "B♭", useFlats: true  }, // 2 flats
  { pc: 3,  name: "E♭", useFlats: true  }, // 3 flats
  { pc: 8,  name: "A♭", useFlats: true  }, // 4 flats
  { pc: 1,  name: "D♭", useFlats: true  }, // 5 flats
];

/**
 * The twelve MINOR keys. Deliberately a different table: minor key signatures
 * don't line up with major ones on the same tonic. A minor has no accidentals
 * while A♭ major has four flats; C minor has three flats while C major has none.
 * Using the major table for minor keys is what used to spell C minor with sharps.
 */
export const MINOR_TONIC_TABLE: TonicEntry[] = [
  { pc: 9,  name: "A",  useFlats: false }, // 0
  { pc: 4,  name: "E",  useFlats: false }, // 1 sharp
  { pc: 11, name: "B",  useFlats: false }, // 2 sharps
  { pc: 6,  name: "F♯", useFlats: false }, // 3 sharps
  { pc: 1,  name: "C♯", useFlats: false }, // 4 sharps
  { pc: 8,  name: "G♯", useFlats: false }, // 5 sharps
  { pc: 2,  name: "D",  useFlats: true  }, // 1 flat
  { pc: 7,  name: "G",  useFlats: true  }, // 2 flats
  { pc: 0,  name: "C",  useFlats: true  }, // 3 flats
  { pc: 5,  name: "F",  useFlats: true  }, // 4 flats
  { pc: 10, name: "B♭", useFlats: true  }, // 5 flats
  { pc: 3,  name: "E♭", useFlats: true  }, // 6 flats
];

export const tonicTableFor = (q: ScaleQuality) =>
  isMinorQuality(q) ? MINOR_TONIC_TABLE : TONIC_TABLE;

/** Scale-degree intervals in semitones from the tonic, per quality. */
const SCALE_INTERVALS: Record<ScaleQuality, number[]> = {
  // Major: W W H W W W H
  "major":          [0, 2, 4, 5, 7, 9, 11],
  // Natural minor: W H W W H W W
  "natural-minor":  [0, 2, 3, 5, 7, 8, 10],
  // Harmonic minor: W H W W H W+H H
  "harmonic-minor": [0, 2, 3, 5, 7, 8, 11],
  // Melodic minor (ascending): W H W W W W H
  "melodic-minor":  [0, 2, 3, 5, 7, 9, 11],
};

export interface KeyState {
  /** Pitch class of the tonic (0..11). */
  tonicPc: number;
  /** Letter name + accidental for the tonic, correct for the current quality. */
  tonicName: string;
  /** Currently selected scale quality. */
  quality: ScaleQuality;
  /** Which accidentals to prefer when naming notes outside the scale. */
  useFlats: boolean;
  /** Qualities that are available to the student in this lesson. */
  availableQualities: ScaleQuality[];
  /** The twelve tonics to offer, named correctly for the current quality. */
  tonicOptions: TonicEntry[];
  /** Set the tonic by pitch class. */
  setTonicPc: (pc: number) => void;
  /** Set the scale quality. */
  setQuality: (q: ScaleQuality) => void;
  /** Returns the absolute pitch class for a scale degree (1-based). */
  pcFromDegree: (degree: number) => number;
  /** Correctly spelled name for a scale degree (1-based) — e.g. 3 of C minor → "E♭". */
  nameForDegree: (degree: number) => string;
  /**
   * Name for any pitch class. Pitches that belong to the current scale are
   * spelled by their degree; anything chromatic falls back to the key's
   * accidental preference.
   */
  nameForPc: (pc: number) => string;
  /** The scale's pitch classes in order, length 7. */
  scalePcs: number[];
  /** The scale's correctly spelled names, in order, length 7. */
  scaleNames: string[];
  /** Diatonic Roman numerals for this quality. */
  romans: string[];
}

const KeyContext = createContext<KeyState | null>(null);

export interface KeyProviderProps {
  /** Default tonic letter (e.g. "C", "G", "F♯", "B♭"). */
  defaultTonic?: string;
  /** Available scale qualities in this lesson. First is the default. */
  availableQualities?: ScaleQuality[];
  children: ReactNode;
}

export function KeyProvider({
  defaultTonic = "C",
  availableQualities = ["major"],
  children,
}: KeyProviderProps) {
  const [quality, setQuality] = useState<ScaleQuality>(availableQualities[0]);

  // Lessons pass ASCII ("Bb", "F#") as often as unicode. Resolve the tonic
  // through the parser so an ASCII spelling can never silently fall back to
  // C major — that would render an entire lesson in the wrong key.
  const initialPc = useMemo(() => parseNote(defaultTonic)?.pc ?? 0, [defaultTonic]);
  const [tonicPc, setTonicPc] = useState<number>(initialPc);

  const minor = isMinorQuality(quality);
  const tonicOptions = minor ? MINOR_TONIC_TABLE : TONIC_TABLE;
  const tonicEntry =
    tonicOptions.find((t) => t.pc === tonicPc) ?? tonicOptions[0];
  const tonicName = tonicEntry.name;
  const useFlats = keyUsesFlats(tonicName, minor);
  const degreeLabels = QUALITY_DEGREES[quality];

  const pcFromDegree = useCallback(
    (degree: number) => {
      const i = ((degree - 1) % 7 + 7) % 7;
      const semis = SCALE_INTERVALS[quality][i];
      return ((tonicPc + semis) % 12 + 12) % 12;
    },
    [tonicPc, quality]
  );

  const nameForDegree = useCallback(
    (degree: number) => {
      const i = ((degree - 1) % 7 + 7) % 7;
      return spellDegree(tonicName, degreeLabels[i]) ?? tonicName;
    },
    [tonicName, degreeLabels]
  );

  const nameForPc = useCallback(
    (pc: number) => spellPcInScale(tonicName, degreeLabels, pc, useFlats),
    [tonicName, degreeLabels, useFlats]
  );

  const scalePcs = useMemo(
    () => SCALE_INTERVALS[quality].map((s) => (tonicPc + s) % 12),
    [tonicPc, quality]
  );

  const scaleNames = useMemo(
    () => degreeLabels.map((d) => spellDegree(tonicName, d) ?? d),
    [tonicName, degreeLabels]
  );

  const romans = useMemo(() => {
    if (quality === "major") return ["I", "ii", "iii", "IV", "V", "vi", "vii°"];
    if (quality === "natural-minor") return ["i", "ii°", "III", "iv", "v", "VI", "VII"];
    if (quality === "harmonic-minor") return ["i", "ii°", "III+", "iv", "V", "VI", "vii°"];
    return ["i", "ii", "III+", "IV", "V", "vi°", "vii°"];
  }, [quality]);

  const value: KeyState = {
    tonicPc,
    tonicName,
    quality,
    useFlats,
    availableQualities,
    tonicOptions,
    setTonicPc,
    setQuality,
    pcFromDegree,
    nameForDegree,
    nameForPc,
    scalePcs,
    scaleNames,
    romans,
  };

  return <KeyContext.Provider value={value}>{children}</KeyContext.Provider>;
}

/** Subscribe to the current key. Returns null if no provider is mounted. */
export function useKey(): KeyState | null {
  return useContext(KeyContext);
}

const MAJOR_DEGREES = QUALITY_DEGREES["major"];

/** Always returns a key state, even if no provider — falls back to C major. */
export function useKeyOrDefault(): KeyState {
  const k = useContext(KeyContext);
  if (k) return k;
  // Synthetic fallback that doesn't update
  return {
    tonicPc: 0,
    tonicName: "C",
    quality: "major",
    useFlats: false,
    availableQualities: ["major"],
    tonicOptions: TONIC_TABLE,
    setTonicPc: () => {},
    setQuality: () => {},
    pcFromDegree: (d) => SCALE_INTERVALS["major"][((d - 1) % 7 + 7) % 7],
    nameForDegree: (d) => spellDegree("C", MAJOR_DEGREES[((d - 1) % 7 + 7) % 7]) ?? "C",
    nameForPc: (pc) => spellPcInScale("C", MAJOR_DEGREES, pc, false),
    scalePcs: SCALE_INTERVALS["major"],
    scaleNames: MAJOR_DEGREES.map((d) => spellDegree("C", d) ?? d),
    romans: ["I","ii","iii","IV","V","vi","vii°"],
  };
}

/** Spell a pitch class with an explicit accidental preference. Re-exported so
 *  components don't each keep their own twelve-name table. */
export { spellPc };
